import { Axios, RawAxiosRequestHeaders } from 'axios';
import { appConfig } from './AppConfig';
import { redirectToSignIn, shouldSkipAuthRedirect } from '../utils/authRedirect';

const defaultHeaders: RawAxiosRequestHeaders = {
    Accept: 'application/json'
};

const apiClient = new Axios({
    baseURL: appConfig.REACT_APP_BACKEND_URL,
    headers: defaultHeaders,
    withCredentials: true,
    validateStatus: (status) => {
        return status < 300;
    },
});

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const requestUrl: string | undefined = error?.config?.url;

        if (status === 401 && !shouldSkipAuthRedirect(requestUrl)) {
            redirectToSignIn();
        }

        return Promise.reject(error);
    },
);

const fetcher = async <ResponsePayload>(
    url: string,
    queryStringParams?: URLSearchParams,
): Promise<ResponsePayload> => {
    return apiClient
        .get(
            `${url}${
                queryStringParams ? '?' + queryStringParams.toString() : ''
            }`,
        )
        .then((response) => JSON.parse(response.data));
};

/**
 * Pull the API's message off a rejected request.
 *
 * `apiClient` is a raw `Axios` instance, which has no `transformResponse`, so
 * `error.response.data` is an unparsed JSON *string* — reading `.message`
 * straight off it always yields undefined and callers silently fall back to
 * axios's generic "Request failed with status code 422". Parse it first.
 */
const apiErrorMessage = (error: any, fallback = 'Something went wrong.'): string => {
    const data = error?.response?.data;

    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            if (parsed?.message) return parsed.message;
        } catch {
            // Not JSON (an HTML error page, say) — fall through.
        }
    } else if (data?.message) {
        return data.message;
    }

    return error?.message ?? fallback;
};

export { fetcher, apiClient, apiErrorMessage };
